// Entity API Client
// Supports both API calls (when available) and localStorage fallback
import { loadEntityData, loadNames, loadTreasure, loadMagicItemTable } from './dataLoader';
import { apiClient } from './client.js';
import { offlineCache } from '../utils/offlineCache';
import { isAuthError } from '@/utils/apiErrors';
import { hasRealAccountSession } from '@/utils/authSession';
import * as homebrewStore from '../utils/homebrewStore.js';
import * as userDataStore from '../utils/userDataStore.js';
import {
  evaluateWildShapeCandidate,
  getBaseWildShapeRules,
  getWildShapeRules,
  monsterChallengeRating,
} from '@/utils/wildShapeRules';

const STORAGE_KEYS = {
  characters: 'mythkit_characters',
  combats: 'mythkit_combats',
  combatParticipants: 'mythkit_combat_participants',
  combatEncounters: 'mythkit_combat_encounters',
};

// Check if API is available (static at load time for non-character entities)
const USE_API = import.meta.env.VITE_USE_API !== 'false'; // Default to true if not set
const API_AVAILABLE = USE_API && typeof window !== 'undefined' && navigator.onLine;

/** Runtime check: online + real account. Anonymous/local play stays on IndexedDB (M10). */
function shouldSyncCharactersToServer() {
  if (typeof navigator === 'undefined' || !navigator.onLine) return false;
  if (!hasRealAccountSession()) return false;
  return USE_API !== 'false';
}

function isNotFoundError(err) {
  return err?.status === 404;
}

/** Drop duplicate IndexedDB rows (same server_id or same local id). Used for campaigns and characters — never merge different server_ids. */
function dedupeIndexedDbRowsByServerOrLocalId(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const seenServer = new Set();
  const seenLocal = new Set();
  const out = [];
  for (const c of rows) {
    const sidRaw = c?.server_id;
    const sid =
      sidRaw != null && sidRaw !== '' && Number.isFinite(Number(sidRaw)) ? Number(sidRaw) : null;
    if (sid != null) {
      if (seenServer.has(sid)) continue;
      seenServer.add(sid);
      out.push(c);
      continue;
    }
    const lid = c?.id != null ? String(c.id) : '';
    if (lid) {
      if (seenLocal.has(lid)) continue;
      seenLocal.add(lid);
    }
    out.push(c);
  }
  return out;
}

function dedupeIndexedDbCampaignRows(rows) {
  return dedupeIndexedDbRowsByServerOrLocalId(rows);
}

function dedupeIndexedDbCharacterRows(rows) {
  return dedupeIndexedDbRowsByServerOrLocalId(rows);
}

/**
 * Pick at most one server campaign to attach to a local row by name.
 * If several share a name, require local.dm_user_id and match primary DM, or return null.
 */
function pickServerCampaignForLocalReconcile(local, nameKey, serverCampaigns) {
  if (!nameKey) return null;
  const matches = (serverCampaigns || []).filter(
    (c) => String(c?.name || '').trim().toLowerCase() === nameKey
  );
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];
  const localDm =
    local?.dm_user_id != null && Number.isFinite(Number(local.dm_user_id))
      ? Number(local.dm_user_id)
      : null;
  if (localDm != null) {
    const byDm = matches.filter((c) => Number(c?.dm_user_id) === localDm);
    if (byDm.length === 1) return byDm[0];
  }
  return null;
}

/** Cached meta (clientDataVersion, cacheVersionEnabled, serverAuthoritative). Fetched once per load. */
let _metaPromise = null;
function getMeta() {
  if (!_metaPromise) _metaPromise = apiClient.getMeta();
  return _metaPromise;
}
export function clearMetaCache() {
  _metaPromise = null;
}

/** Clear offline mirror stores for user characters/campaigns so they cannot diverge from userDataStore after server snapshot. */
async function purgeUserMirrorOfflineCaches() {
  try {
    await offlineCache.clearCache('characters');
    await offlineCache.clearCache('campaigns');
  } catch {
    // non-fatal
  }
}

/** Build request body for POST/PUT /api/characters (server uses name, level, race, class, subclass, background; rest → character_data). */
function buildCharacterServerPayload(character) {
  if (!character || typeof character !== 'object') return null;
  const out = { ...character };
  delete out.id;
  delete out.server_id;
  delete out.share_code;
  const rawName = out.name;
  const trimmed = rawName != null && String(rawName).trim() !== '' ? String(rawName).trim() : '';
  out.name = trimmed || 'Character';
  out.level = out.level ?? 1;
  return out;
}

const CHARACTER_PATCH_TOP_LEVEL = new Set([
  'name',
  'level',
  'race',
  'class',
  'subclass',
  'background',
  'campaign_id',
  'hit_dice',
  'hit_dice_max',
]);

/** PATCH body for PUT /api/characters — changed fields only, so stale local equipment cannot wipe server data. */
function buildCharacterServerPatch(existing, patch) {
  if (!patch || typeof patch !== 'object') return null;
  const out = {
    name: String(existing?.name ?? patch?.name ?? 'Character').trim() || 'Character',
    level: existing?.level ?? patch?.level ?? 1,
  };
  for (const key of ['race', 'class', 'subclass', 'background']) {
    if (patch[key] !== undefined) out[key] = patch[key];
    else if (existing?.[key] !== undefined) out[key] = existing[key];
  }
  // hit_dice is a DB column the worker does not UPDATE; persist spend/recover in character_data as hitDice.
  const hdPatch = patch.hitDice !== undefined ? patch.hitDice : patch.hit_dice;
  if (hdPatch !== undefined) out.hitDice = hdPatch;
  for (const [key, value] of Object.entries(patch)) {
    if (
      key === 'id' ||
      key === 'server_id' ||
      key === 'share_code' ||
      key === 'created_at' ||
      key === 'updated_at' ||
      key === 'user_id' ||
      key === 'character_data' ||
      CHARACTER_PATCH_TOP_LEVEL.has(key)
    ) {
      continue;
    }
    out[key] = value;
  }
  return out;
}

/** Merge server-authoritative fields from POST/PUT/GET character responses into local IndexedDB rows. */
function mergeServerCharacterIntoLocal(local, serverChar) {
  if (!local || !serverChar) return local;
  const next = { ...local };
  if (serverChar.id != null) {
    const sid = parseInt(String(serverChar.id), 10);
    if (!Number.isNaN(sid)) next.server_id = sid;
  }
  if (serverChar.share_code != null && serverChar.share_code !== '') {
    next.share_code = serverChar.share_code;
  }
  const skipKeys = new Set(['id', 'user_id', 'created_at', 'updated_at', 'character_data']);
  for (const [key, value] of Object.entries(serverChar)) {
    if (skipKeys.has(key)) continue;
    if (value !== undefined) next[key] = value;
  }
  if (serverChar.character_data && typeof serverChar.character_data === 'object') {
    Object.assign(next, serverChar.character_data);
  }
  return next;
}

/** Merge the latest server snapshot into a local row before applying patches (avoids stale IndexedDB wiping shop loot). */
async function hydrateLocalCharacterFromServer(local) {
  if (!local || !shouldSyncCharactersToServer()) return local;
  const serverId = resolveLocalServerNumericId(local);
  if (serverId == null) return local;
  try {
    const fromServer = await apiClient.getCharacter(serverId, {});
    if (!fromServer) return local;
    return mergeServerCharacterIntoLocal(local, fromServer);
  } catch {
    return local;
  }
}

async function resolveCharacterForUpdate(id) {
  let existing = await userDataStore.getCharacter(id);
  if (existing) return existing;

  const idStr = String(id ?? '').trim();
  const numId = /^\d+$/.test(idStr) ? parseInt(idStr, 10) : null;
  const all = await userDataStore.getAllCharacters();
  if (numId != null) {
    existing =
      all.find((c) => c.server_id === numId || String(c.id) === idStr) ?? null;
  }
  if (existing) return existing;

  if (!shouldSyncCharactersToServer() || numId == null) return null;
  try {
    const fromServer = await apiClient.getCharacter(numId);
    if (!fromServer) return null;
    const hydrated = {
      ...fromServer,
      id: idStr,
      server_id: numId,
    };
    await userDataStore.putCharacter(hydrated);
    return hydrated;
  } catch {
    return null;
  }
}

/** Merge a server character snapshot into IndexedDB (e.g. after shop purchase updates currency on the server). */
export async function applyServerCharacterSnapshot(characterId, serverChar) {
  if (!serverChar) return null;
  let existing = await resolveCharacterForUpdate(characterId);
  const idStr = String(existing?.id ?? characterId);
  const merged = existing
    ? mergeServerCharacterIntoLocal(existing, serverChar)
    : { ...serverChar, id: idStr };
  const sid =
    merged.server_id ??
    (serverChar.id != null ? parseInt(String(serverChar.id), 10) : undefined);
  const full = {
    ...merged,
    ...serverChar,
    id: idStr,
    ...(sid != null && !Number.isNaN(sid) ? { server_id: sid } : {}),
  };
  await userDataStore.putCharacter(full);
  return full;
}

/** Resolve DB character id for matching server list rows (IndexedDB may only have numeric string id). */
function resolveLocalServerNumericId(local) {
  if (!local) return null;
  if (local.server_id != null && local.server_id !== '') {
    const n = Number(local.server_id);
    return Number.isFinite(n) ? n : null;
  }
  const raw = local.id;
  if (raw != null && /^\d+$/.test(String(raw).trim())) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Fetch server list and merge share_code (and server_id) into local rows — fixes stale clients that only stored server_id. */
async function mergeShareCodesFromServerList() {
  if (!shouldSyncCharactersToServer()) return;
  try {
    const serverList = await apiClient.getCharacters();
    if (!Array.isArray(serverList) || serverList.length === 0) return;
    const byServerId = new Map(
      serverList.map((c) => {
        const id = c?.id != null ? Number(c.id) : NaN;
        return Number.isFinite(id) ? [id, c] : null;
      }).filter(Boolean)
    );
    const locals = await userDataStore.getAllCharacters();
    for (const local of locals || []) {
      const sid = resolveLocalServerNumericId(local);
      if (sid == null) continue;
      const s = byServerId.get(sid);
      if (!s) continue;
      const merged = mergeServerCharacterIntoLocal(
        { ...local, server_id: local.server_id ?? sid },
        s
      );
      if (merged.share_code !== local.share_code || merged.server_id !== local.server_id) {
        await userDataStore.putCharacter(merged);
      }
    }
  } catch {
    // ignore
  }
}

async function hydrateShareCodeFromServerIfNeeded(local) {
  if (!local) return local;
  if (local.share_code) return local;
  if (!shouldSyncCharactersToServer()) return local;
  const sid = resolveLocalServerNumericId(local);
  if (sid == null) return local;
  try {
    const full = await apiClient.getCharacter(sid, {});
    if (full?.share_code) {
      const merged = mergeServerCharacterIntoLocal(
        { ...local, server_id: local.server_id ?? sid },
        full
      );
      await userDataStore.putCharacter(merged);
      return merged;
    }
  } catch {
    // ignore
  }
  return local;
}

/** Fire-and-forget: sync all local characters to the server with full data (create or update by server_id). */
function triggerCharacterSyncToServer() {
  if (typeof navigator === 'undefined' || !navigator.onLine) return;
  if (!hasRealAccountSession()) return;
  userDataStore.getAllCharacters()
    .then((list) => {
      if (!list?.length) return;
      return Promise.all(
        list.map((local) => {
          const payload = buildCharacterServerPayload(local);
          if (!payload) return Promise.resolve();
          if (local.server_id) {
            // Do not push full local snapshots for existing server rows — stale equipment/currency can overwrite shop purchases.
            return Promise.resolve();
          }
          return apiClient.createCharacter(payload).then((res) => {
            if (res?.id) {
              const merged = mergeServerCharacterIntoLocal(
                { ...local, server_id: parseInt(res.id, 10) },
                res
              );
              return userDataStore.putCharacter(merged);
            }
          }).catch(() => {});
        })
      );
    })
    .catch(() => {});
}

/**
 * Sync all local (IndexedDB) characters to the server with full data.
 * Returns { synced } count. Use from My Characters "Sync Local" button.
 */
export async function syncAllCharactersToServer() {
  if (!shouldSyncCharactersToServer()) return { synced: 0, failed: 0, errors: [] };
  const list = await userDataStore.getAllCharacters();
  if (!list?.length) return { synced: 0, failed: 0, errors: [] };
  let synced = 0;
  let failed = 0;
  const errors = [];
  for (const local of list) {
    const payload = buildCharacterServerPayload(local);
    if (!payload) {
      failed++;
      errors.push({ id: local?.id, message: 'Invalid character data' });
      continue;
    }
    try {
      if (local.server_id) {
        try {
          const res = await apiClient.updateCharacter(local.server_id, payload);
          if (res) await userDataStore.putCharacter(mergeServerCharacterIntoLocal(local, res));
          synced++;
        } catch (e) {
          if (!isNotFoundError(e)) throw e;
          const created = await apiClient.createCharacter(payload);
          if (created?.id) {
            const merged = mergeServerCharacterIntoLocal(
              { ...local, server_id: parseInt(created.id, 10) },
              created
            );
            await userDataStore.putCharacter(merged);
            synced++;
          } else {
            failed++;
            errors.push({ id: local?.id, message: 'Server did not return a character id' });
          }
        }
      } else {
        const res = await apiClient.createCharacter(payload);
        if (res?.id) {
          const merged = mergeServerCharacterIntoLocal(
            { ...local, server_id: parseInt(res.id, 10) },
            res
          );
          await userDataStore.putCharacter(merged);
          synced++;
        } else {
          failed++;
          errors.push({ id: local?.id, message: 'Server did not return a character id' });
        }
      }
    } catch (e) {
      failed++;
      const msg = e?.message || e?.error || 'Sync failed';
      errors.push({ id: local?.id, message: String(msg) });
    }
  }
  await mergeShareCodesFromServerList();
  return { synced, failed, errors };
}

/**
 * Resolve or create the server row for a local character before campaign linking.
 * Reuses an existing server character with the same name when unambiguous.
 * @returns {{ serverId: string, localId: string }}
 */
export async function ensureCharacterServerId(characterOrId) {
  let character =
    characterOrId != null && typeof characterOrId === 'object'
      ? characterOrId
      : await userDataStore.getCharacter(String(characterOrId));

  if (!character) {
    const id = String(characterOrId ?? '').trim();
    if (/^\d+$/.test(id)) {
      return { serverId: id, localId: id };
    }
    throw new Error('Character not found');
  }

  const localId = String(character.id);
  if (character.server_id != null && Number.isFinite(Number(character.server_id))) {
    return { serverId: String(character.server_id), localId };
  }
  if (/^\d+$/.test(localId)) {
    return { serverId: localId, localId };
  }

  if (!shouldSyncCharactersToServer()) {
    throw new Error('Character must be synced to the server before linking to a campaign');
  }

  const nameKey = String(character.name || '').trim().toLowerCase();
  let serverList = [];
  try {
    serverList = await apiClient.getCharacters();
  } catch {
    serverList = [];
  }
  if (!Array.isArray(serverList)) serverList = [];

  if (nameKey) {
    const matches = serverList.filter(
      (c) => String(c?.name || '').trim().toLowerCase() === nameKey
    );
    if (matches.length === 1 && matches[0]?.id != null) {
      const sid = parseInt(String(matches[0].id), 10);
      const merged = mergeServerCharacterIntoLocal({ ...character, server_id: sid }, matches[0]);
      await userDataStore.putCharacter(merged);
      const payload = buildCharacterServerPayload(character);
      if (payload) {
        try {
          const updated = await apiClient.updateCharacter(sid, payload);
          if (updated) {
            await userDataStore.putCharacter(mergeServerCharacterIntoLocal(merged, updated));
          }
        } catch {
          // keep reconciled id
        }
      }
      return { serverId: String(sid), localId };
    }
  }

  const payload = buildCharacterServerPayload(character);
  if (!payload) {
    throw new Error('Invalid character data');
  }

  const created = await apiClient.createCharacter(payload);
  if (!created?.id) {
    throw new Error('Server did not return a character id');
  }
  const sid = parseInt(String(created.id), 10);
  const merged = mergeServerCharacterIntoLocal(
    { ...character, server_id: sid },
    created
  );
  await userDataStore.putCharacter(merged);
  return { serverId: String(created.id), localId };
}

/**
 * Map local character row id (UUID string) → server numeric id after sync.
 * @returns {Promise<Map<string, number>>}
 */
export async function buildCharacterLocalToServerIdMap() {
  const map = new Map();
  try {
    const list = await userDataStore.getAllCharacters();
    for (const c of list || []) {
      if (c?.server_id == null || !Number.isFinite(Number(c.server_id))) continue;
      map.set(String(c.id), Number(c.server_id));
    }
  } catch {
    // ignore
  }
  return map;
}

/**
 * Push local-only campaigns (non-numeric ids) to the API, or reconcile by name to an existing server row.
 * Removes old local keys from IndexedDB when a server row is adopted.
 * @returns {Promise<{ synced: number, reconciled: number }>}
 */
export async function syncLocalCampaignsToServer() {
  if (!shouldSyncCharactersToServer()) return { synced: 0, reconciled: 0 };
  let serverList = [];
  try {
    const raw = await apiClient.getCampaigns();
    serverList = Array.isArray(raw) ? raw : [];
  } catch {
    return { synced: 0, reconciled: 0 };
  }
  const localCampaigns = await userDataStore.getAllCampaigns();
  if (!Array.isArray(localCampaigns) || localCampaigns.length === 0) {
    return { synced: 0, reconciled: 0 };
  }
  let synced = 0;
  let reconciled = 0;
  for (const local of localCampaigns) {
    const localIdStr = String(local.id ?? '');
    const existingServerId =
      local.server_id != null && Number.isFinite(Number(local.server_id))
        ? Number(local.server_id)
        : /^\d+$/.test(localIdStr)
          ? Number(localIdStr)
          : null;
    if (existingServerId != null && serverList.some((c) => Number(c.id) === existingServerId)) {
      reconciled++;
      continue;
    }
    const nameKey = String(local.name || '').trim().toLowerCase();
    const nameMatch = pickServerCampaignForLocalReconcile(local, nameKey, serverList);
    if (nameMatch?.id != null) {
      try {
        await userDataStore.removeCampaign(localIdStr);
        await userDataStore.putCampaign({
          ...local,
          ...nameMatch,
          id: String(nameMatch.id),
          server_id: Number(nameMatch.id),
        });
      } catch {
        // non-fatal
      }
      reconciled++;
      continue;
    }
    const looksLocalOnly = localIdStr && !/^\d+$/.test(localIdStr);
    if (existingServerId == null && looksLocalOnly) {
      try {
        const created = await apiClient.createCampaign({
          name: local.name || 'Campaign',
          description: local.description ?? '',
          rule_settings:
            local.rule_settings && typeof local.rule_settings === 'object' ? local.rule_settings : {},
        });
        if (created?.id != null) {
          await userDataStore.removeCampaign(localIdStr);
          const sid = Number(created.id);
          await userDataStore.putCampaign({
            ...local,
            ...created,
            id: String(created.id),
            server_id: Number.isFinite(sid) ? sid : created.id,
          });
          serverList.push(created);
          synced++;
        }
      } catch {
        // skip
      }
    }
  }
  return { synced, reconciled };
}

/**
 * Reconnect sync: when serverAuthoritative is OFF, push local-only characters to the server
 * (full character payload), reconcile characters by name to avoid duplicates, and link local
 * campaigns to existing server rows by id or an unambiguous name match. Does not POST new
 * campaigns (that caused duplicate server campaigns for every device that had a stale local row).
 */
export async function runReconnectSync() {
  if (!shouldSyncCharactersToServer()) return;
  const meta = await getMeta();
  if (meta.serverAuthoritative) {
    return;
  }
  let serverCampaigns = [];
  let serverCharacters = [];
  try {
    serverCampaigns = await apiClient.getCampaigns();
    serverCharacters = await apiClient.getCharacters();
  } catch {
    return;
  }
  if (!Array.isArray(serverCampaigns)) serverCampaigns = [];
  if (!Array.isArray(serverCharacters)) serverCharacters = [];

  const serverCampaignById = new Map(serverCampaigns.map((c) => [Number(c.id), c]));
  const serverCharacterById = new Map(serverCharacters.map((c) => [Number(c.id), c]));
  /** @type {Map<string, any[]>} */
  const serverCharacterByName = new Map();
  for (const c of serverCharacters) {
    const key = String(c.name || '').trim().toLowerCase();
    if (!key) continue;
    if (!serverCharacterByName.has(key)) serverCharacterByName.set(key, []);
    serverCharacterByName.get(key).push(c);
  }

  const localCampaigns = await userDataStore.getAllCampaigns();
  const localCharacters = await userDataStore.getAllCharacters();
  const campaignMap = new Map(); // localId -> serverId
  const characterMap = new Map(); // localId -> serverId

  for (const local of localCampaigns || []) {
    const localId = String(local.id);
    const serverId = local.server_id ?? (local.id != null && Number.isInteger(Number(local.id)) ? Number(local.id) : null);
    if (serverId != null && serverCampaignById.has(serverId)) {
      campaignMap.set(localId, serverId);
      continue;
    }
    const nameKey = String(local.name || '').trim().toLowerCase();
    const nameMatch = pickServerCampaignForLocalReconcile(local, nameKey, serverCampaigns);
    if (nameMatch?.id != null) {
      const sid = Number(nameMatch.id);
      campaignMap.set(localId, sid);
      await userDataStore.putCampaign({ ...local, server_id: sid });
    }
  }

  for (const local of localCharacters || []) {
    const localId = String(local.id);
    const serverId = local.server_id ?? (local.id != null && Number.isInteger(Number(local.id)) ? Number(local.id) : null);
    if (serverId != null && serverCharacterById.has(serverId)) {
      characterMap.set(localId, serverId);
      const payload = buildCharacterServerPayload(local);
      if (payload) {
        try {
          const updated = await apiClient.updateCharacter(serverId, payload);
          if (updated) await userDataStore.putCharacter(mergeServerCharacterIntoLocal(local, updated));
        } catch {
          // skip
        }
      }
      continue;
    }
    const nameKey = String(local.name || '').trim().toLowerCase();
    const nameMatches = nameKey ? serverCharacterByName.get(nameKey) : null;
    const existing =
      Array.isArray(nameMatches) && nameMatches.length === 1 ? nameMatches[0] : null;
    if (nameKey && existing) {
      characterMap.set(localId, existing.id);
      await userDataStore.putCharacter({ ...local, server_id: existing.id });
      const payload = buildCharacterServerPayload(local);
      if (payload) {
        try {
          const updated = await apiClient.updateCharacter(existing.id, payload);
          if (updated) await userDataStore.putCharacter(mergeServerCharacterIntoLocal({ ...local, server_id: existing.id }, updated));
        } catch {
          // skip
        }
      }
      continue;
    }
    if (nameKey && Array.isArray(nameMatches) && nameMatches.length > 1) {
      continue;
    }
    const payload = buildCharacterServerPayload(local);
    if (!payload) continue;
    try {
      const created = await apiClient.createCharacter(payload);
      if (created?.id) {
        const sid = typeof created.id === 'number' ? created.id : parseInt(created.id, 10);
        characterMap.set(localId, sid);
        const merged = mergeServerCharacterIntoLocal({ ...local, server_id: sid }, created);
        await userDataStore.putCharacter(merged);
      }
    } catch {
      // skip
    }
  }
}

function getFromStorage(key) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    return [];
  }
}

function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    // Silent fail
  }
}

// Transform API monster row (from GET /api/srd/monsters/:id) to { meta, data } shape expected by campaign combat components
function apiMonsterToMetaData(monster) {
  if (!monster) return null;
  const languages = monster.languages
    ? (Array.isArray(monster.languages) ? monster.languages : String(monster.languages).split(',').map((l) => l.trim()))
    : [];
  return {
    meta: {
      slug: monster.slug || String(monster.id),
      name: monster.name,
      type: 'monster',
      source: monster.source || null,
    },
    data: {
      size: monster.size,
      type: monster.type,
      alignment: monster.alignment,
      challenge_rating: monster.challenge_rating,
      challenge: monster.challenge_rating != null ? { cr: monster.challenge_rating } : null,
      ac: monster.armor_class != null ? { value: monster.armor_class, notes: monster.armor_description || null } : null,
      hp: monster.hit_points_average != null ? { average: monster.hit_points_average, formula: monster.hit_points_dice || null } : null,
      speeds: monster.speed || {},
      ability_scores: monster.ability_scores || {},
      proficiencies: {
        saving_throws: monster.saving_throws || null,
        skills: monster.skills || null,
      },
      immunities: monster.damage_immunities || [],
      resistances: monster.damage_resistances || [],
      vulnerabilities: monster.damage_vulnerabilities || [],
      condition_immunities: monster.condition_immunities || [],
      senses: monster.senses || {},
      languages,
      traits: monster.traits || [],
      actions: monster.actions || [],
      reactions: monster.reactions || [],
      legendary_actions: monster.legendary_actions || [],
      description: monster.description,
    },
  };
}

// Transform flat Monster entity (from dataLoader/list()) to { meta, data } for campaign combat components
function flatMonsterToMetaData(flat) {
  if (!flat) return null;
  return {
    meta: {
      slug: flat.id,
      name: flat.name,
      type: 'monster',
      source: flat.source || null,
    },
    data: {
      size: flat.size,
      type: flat.type,
      alignment: flat.alignment,
      challenge_rating: flat.challenge_rating,
      challenge: flat.challenge_rating != null ? { cr: flat.challenge_rating } : null,
      ac: flat.armor_class != null ? { value: flat.armor_class, notes: flat.armor_description || null } : null,
      hp: flat.hit_points,
      speeds: flat.speed || {},
      ability_scores: flat.ability_scores || {},
      proficiencies: {
        saving_throws: flat.saving_throws || null,
        skills: flat.skills || null,
      },
      immunities: flat.damage_immunities || [],
      resistances: flat.damage_resistances || [],
      vulnerabilities: flat.damage_vulnerabilities || [],
      condition_immunities: flat.condition_immunities || [],
      senses: flat.senses || {},
      languages: flat.languages,
      traits: flat.traits || [],
      actions: flat.actions || [],
      reactions: flat.reactions || [],
      legendary_actions: flat.legendary_actions || [],
      description: flat.description,
    },
  };
}

// Map compendium entity names to homebrew API type param
const ENTITY_TO_HOMEBREW_TYPE = {
  Monster: 'monster',
  Spell: 'spell',
  MagicItem: ['item', 'weapon', 'armor'],
  Race: 'race',
  Class: 'class',
  Subclass: 'subclass',
  Background: 'background',
  Feat: 'feat',
  Language: 'language',
};

async function mergeHomebrewIntoList(entityName, baseList) {
  const homebrewType = ENTITY_TO_HOMEBREW_TYPE[entityName];
  if (!homebrewType || !Array.isArray(baseList)) return baseList;
  const types = Array.isArray(homebrewType) ? homebrewType : [homebrewType];
  let homebrew = [];
  try {
    const lists = await Promise.all(types.map((t) => homebrewStore.getByType(t)));
    homebrew = lists.flat().filter(Boolean);
  } catch (_) {
    // ignore
  }
  if (!Array.isArray(homebrew) || homebrew.length === 0) return baseList;
  const normalized = homebrew.map((h) => adaptHomebrewEntity(entityName, {
    id: String(h.id),
    name: h.name || h.data?.name || '',
    source: 'Homebrew',
    is_owner: h.is_owner ?? true,
    linked_permission: h.linked_permission ?? null,
    homebrew_scope: h.is_owner ? 'owned' : 'shared',
    ...(h.data || {}),
  }));
  const byId = new Map(baseList.map((item) => [String(item.id), item]));
  normalized.forEach((item) => byId.set(String(item.id), item));
  return Array.from(byId.values());
}

function abilityBonusesFromAsi(asi) {
  const out = {};
  if (!asi || typeof asi !== 'object') return out;
  Object.entries(asi).forEach(([key, value]) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return;
    out[key] = num;
    const map = {
      STR: 'strength',
      DEX: 'dexterity',
      CON: 'constitution',
      INT: 'intelligence',
      WIS: 'wisdom',
      CHA: 'charisma',
    };
    const upper = String(key).toUpperCase();
    out[upper] = num;
    if (map[upper]) out[map[upper]] = num;
  });
  return out;
}

function adaptHomebrewEntity(entityName, item) {
  if (!item || typeof item !== 'object') return item;
  if (entityName === 'Race') {
    return {
      ...item,
      speed: item.speed ?? item.speeds?.walk ?? 30,
      languages: Array.isArray(item.languages) ? item.languages : [],
      traits: Array.isArray(item.traits) ? item.traits : [],
      ability_bonuses: item.ability_bonuses || abilityBonusesFromAsi(item.ability_score_increase),
    };
  }
  if (entityName === 'Class') {
    const rawSkillChoices = item.skill_choices;
    const normalizedSkillChoices = rawSkillChoices && typeof rawSkillChoices === 'object'
      ? {
          ...rawSkillChoices,
          choose: Number(rawSkillChoices.choose ?? rawSkillChoices.count ?? 0),
          from: Array.isArray(rawSkillChoices.from)
            ? rawSkillChoices.from
            : Array.isArray(rawSkillChoices.available)
              ? rawSkillChoices.available
              : [],
        }
      : null;
    return {
      ...item,
      hit_die: item.hit_die ?? 8,
      saving_throw_proficiencies: item.saving_throw_proficiencies || item.saving_throws || [],
      skill_choices: normalizedSkillChoices,
      features_by_level: Array.isArray(item.features_by_level) ? item.features_by_level : [],
    };
  }
  if (entityName === 'Subclass') {
    return {
      ...item,
      features_by_level: Array.isArray(item.features_by_level) ? item.features_by_level : [],
    };
  }
  if (entityName === 'Background') {
    return {
      ...item,
      skill_proficiencies: item.skill_proficiencies || [],
      tool_proficiencies: item.tool_proficiencies || [],
    };
  }
  if (entityName === 'Feat') {
    return {
      ...item,
      benefits: Array.isArray(item.benefits) ? item.benefits : [],
    };
  }
  if (entityName === 'Monster') {
    return {
      ...item,
      challenge_rating: item.challenge_rating ?? item.cr ?? 0,
      armor_class: item.armor_class ?? item.ac ?? 10,
    };
  }
  if (entityName === 'Spell') {
    const components = item.components;
    const componentList = Array.isArray(components)
      ? components.map((v) => String(v || '').toUpperCase())
      : [];
    const componentObj = components && typeof components === 'object' && !Array.isArray(components)
      ? components
      : {};
    return {
      ...item,
      school: typeof item.school === 'string' ? item.school.trim() : item.school,
      classes: Array.isArray(item.classes)
        ? item.classes.map((v) => String(v).trim()).filter(Boolean)
        : [],
      components: {
        verbal: componentList.includes('V') || !!componentObj.verbal || !!componentObj.v,
        somatic: componentList.includes('S') || !!componentObj.somatic || !!componentObj.s,
        material: componentList.includes('M') || !!componentObj.material || !!componentObj.m,
        ...(((componentObj.materials_needed || (typeof componentObj.m === 'string' ? componentObj.m : '')) && (componentList.includes('M') || !!componentObj.material || !!componentObj.m))
          ? { materials_needed: String(componentObj.materials_needed || componentObj.m).trim() }
          : {}),
      },
    };
  }
  return item;
}

function createEntity(entityName) {
  const entityLower = entityName.toLowerCase();
  const entityToStorageKey = {
    'character': 'characters',
    'combat': 'combats',
    'combatparticipant': 'combatParticipants',
    'combatencounter': 'combatEncounters',
  };
  const storageKey = STORAGE_KEYS[entityToStorageKey[entityLower] || entityLower];
  const apiEntityName = entityLower === 'character' ? 'characters' : 
                        entityLower === 'combat' ? 'combats' :
                        entityLower === 'combatparticipant' ? 'combatParticipants' :
                        entityLower === 'combatencounter' ? 'encounters' : entityLower;

  return {
    async list(sort = null, options = {}) {
      const localOnly = options?.localOnly === true;
      const entityTypeMap = {
        'Race': 'Race',
        'Class': 'Class',
        'Subclass': 'Subclass',
        'Spell': 'Spell',
        'Background': 'Background',
        'Monster': 'Monster',
        'MagicItem': 'MagicItem',
        'Feat': 'Feat',
        'Language': 'Language',
      };

      // SRD data - local only (compendium store + dataLoader), never from API
      if (entityTypeMap[entityName]) {
        const data = await loadEntityData(entityTypeMap[entityName]);
        const merged = await mergeHomebrewIntoList(entityName, data);
        if (sort) {
          return this.sortData(merged, sort);
        }
        return merged;
      }

      // User data (characters, campaigns) - when server-authoritative: fetch from API and replace IndexedDB
      if (entityName === 'Character') {
        const meta = await getMeta();
        const isAuth = hasRealAccountSession();
        const online = typeof navigator !== 'undefined' && navigator.onLine;
        if (meta.serverAuthoritative && isAuth && online && USE_API !== 'false') {
          try {
            // Server-authoritative online mode: server list is canonical; reconnect push runs from sync coordinator on online only.
            const serverList = await apiClient.getCharacters();
            const raw = Array.isArray(serverList) ? serverList : [];
            const list = raw.map((c) => ({
              ...c,
              id: String(c.id ?? c.server_id ?? ''),
              server_id: c.id ?? c.server_id,
            }));
            await userDataStore.replaceAllCharacters(list);
            await purgeUserMirrorOfflineCaches();
            if (sort) return this.sortData(list, sort);
            return list;
          } catch (err) {
            if (isAuthError(err)) throw err;
          }
        }
        if (isAuth && online && USE_API !== 'false') {
          try {
            await mergeShareCodesFromServerList();
          } catch {
            // ignore
          }
        }
        const data = dedupeIndexedDbCharacterRows(await userDataStore.getAllCharacters());
        if (sort) return this.sortData(data, sort);
        return data;
      }
      if (entityName === 'Campaign') {
        const meta = await getMeta();
        const isAuth = hasRealAccountSession();
        const online = typeof navigator !== 'undefined' && navigator.onLine;
        if (meta.serverAuthoritative && isAuth && online && USE_API !== 'false') {
          try {
            const serverList = await apiClient.getCampaigns();
            const raw = Array.isArray(serverList) ? serverList : [];
            const list = raw.map((c) => ({
              ...c,
              id: String(c.id ?? c.server_id ?? ''),
              server_id: c.id ?? c.server_id,
            }));
            await userDataStore.replaceAllCampaigns(list);
            await purgeUserMirrorOfflineCaches();
            if (sort) return this.sortData(list, sort);
            return list;
          } catch (err) {
            if (isAuthError(err)) throw err;
          }
        }
        const data = dedupeIndexedDbCampaignRows(await userDataStore.getAllCampaigns());
        if (sort) return this.sortData(data, sort);
        return data;
      }

      // Other user data (combats, etc.) - try API first, fallback to localStorage
      if (!localOnly && API_AVAILABLE && storageKey) {
        try {
          // Use the proper API client method for characters
          let data;
          if (entityName === 'Character') {
            data = await apiClient.getCharacters();
          } else {
            data = await apiClient.get(`/api/${apiEntityName}`);
          }
          
          // Also cache for offline
          if (offlineCache.isOnline()) {
            await Promise.all(data.map(item => offlineCache.cacheEntity(storageKey, item)));
          }
          if (sort) {
            return this.sortData(data, sort);
          }
          return data;
        } catch (error) {
          if (isAuthError(error)) {
            const token = localStorage.getItem('auth_token');
            if (token) {
              throw new Error('Authentication expired. Please log in again.');
            }
          }
        }
      }

      // Fallback to localStorage or IndexedDB cache
      if (storageKey) {
        // Try IndexedDB cache first
        try {
          const cached = await offlineCache.getAllCached(storageKey);
          if (cached.length > 0) {
            if (sort) {
              return this.sortData(cached, sort);
            }
            return cached;
          }
        } catch (error) {
          // Silently fall through to localStorage
        }
        
        // Fallback to localStorage
        const data = getFromStorage(storageKey);
        if (sort) {
          return this.sortData(data, sort);
        }
        return data;
      }

      return [];
    },

    sortData(data, sort) {
      if (!sort) return data;
      if (sort.startsWith('-')) {
        const field = sort.substring(1);
        return [...data].sort((a, b) => {
          const aVal = a[field] || '';
          const bVal = b[field] || '';
          return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
        });
      } else {
        return [...data].sort((a, b) => {
          const aVal = a[sort] || '';
          const bVal = b[sort] || '';
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        });
      }
    },
    
    async get(id, options = {}) {
      // Characters: try server first so combat/party can pull by server id; fallback to local when offline or 404.
      if (entityName === 'Character') {
        // Backend stores `characters.id` as SERIAL (integer). Our local characters use UUID-like IDs.
        // Avoid calling the API with local UUIDs (will throw SQL "invalid input syntax" and return 500).
        const looksLikeServerId =
          typeof id === 'number'
            ? Number.isFinite(id)
            : typeof id === 'string'
              ? /^\d+$/.test(id.trim())
              : false;

        if (API_AVAILABLE && looksLikeServerId) {
          try {
            const item = await apiClient.getCharacter(id, options);
            if (item) {
              return mergeServerCharacterIntoLocal({ id: String(id).trim() }, item);
            }
            return item;
          } catch (error) {
            // Fall back to local character cache on request errors.
          }
        }
        let local = await userDataStore.getCharacter(id);
        if (local) {
          const hydrated = await hydrateLocalCharacterFromServer(local);
          if (hydrated !== local) {
            await userDataStore.putCharacter(hydrated);
            local = hydrated;
          }
          return await hydrateShareCodeFromServerIfNeeded(local);
        }
        const numId = parseInt(id, 10);
        if (!Number.isNaN(numId)) {
          const all = await userDataStore.getAllCharacters();
          const byServerId = all.find((c) => c.server_id === numId);
          if (byServerId) return await hydrateShareCodeFromServerIfNeeded(byServerId);
        }
        return null;
      }

      if (entityName === 'Campaign') {
        return userDataStore.getCampaign(id);
      }

      if (API_AVAILABLE && storageKey) {
        try {
          const item = await apiClient.get(`/api/${apiEntityName}/${id}`);
          
          // Cache for offline
          if (offlineCache.isOnline()) {
            await offlineCache.cacheEntity(storageKey, item);
          }
          return item;
        } catch (error) {
          if (isAuthError(error)) {
            const token = localStorage.getItem('auth_token');
            if (token) {
              throw new Error('Authentication expired. Please log in again.');
            }
          }
        }
      }

      // Try IndexedDB cache
      if (storageKey) {
        try {
          const cached = await offlineCache.getCachedEntity(storageKey, id);
          if (cached) return cached;
        } catch (error) {
          // Silently fall through to localStorage
        }
      }

      // SRD get-by-id: Monster (and optionally Spell, Race) — try API first, then list-then-find
      const entityTypeMap = {
        Race: 'Race',
        Class: 'Class',
        Subclass: 'Subclass',
        Spell: 'Spell',
        Background: 'Background',
        Monster: 'Monster',
        MagicItem: 'MagicItem',
        Feat: 'Feat',
      };
      if (entityTypeMap[entityName]) {
        // Monster: resolve from list then convert to { meta, data } for campaign combat components
        if (entityName === 'Monster') {
          const all = await this.list();
          const flat = all.find((item) => item.id === id || item.id === parseInt(id, 10) || String(item.id) === String(id)) || null;
          return flat ? flatMonsterToMetaData(flat) : null;
        }
        // Other SRD entities: list then find by id
        const all = await this.list();
        return all.find((item) => item.id === id || item.id === parseInt(id, 10) || String(item.id) === String(id)) || null;
      }

      // Fallback to localStorage or local list (user data)
      const all = await this.list();
      return all.find(item => item.id === id) || null;
    },
    
    async create(data, options = {}) {
      if (entityName === 'Character') {
        const result = await userDataStore.putCharacter(data);
        if (shouldSyncCharactersToServer()) {
          try {
            const payload = buildCharacterServerPayload(result);
            if (payload) {
              const serverChar = await apiClient.createCharacter(payload);
              if (serverChar?.id) {
                const withServer = mergeServerCharacterIntoLocal(
                  { ...result, server_id: parseInt(serverChar.id, 10) },
                  serverChar
                );
                await userDataStore.putCharacter(withServer);
                return withServer;
              }
            }
          } catch (e) {
            triggerCharacterSyncToServer();
          }
        }
        return result;
      }
      if (entityName === 'Campaign') {
        const isAuth = hasRealAccountSession();
        const online = typeof navigator !== 'undefined' && navigator.onLine;
        if (isAuth && online && USE_API !== 'false') {
          try {
            const body = {
              name: data.name || 'Campaign',
              description: data.description ?? '',
              rule_settings:
                data.rule_settings && typeof data.rule_settings === 'object' ? data.rule_settings : {},
            };
            const created = await apiClient.createCampaign(body);
            if (created && created.id != null) {
              const sid = typeof created.id === 'number' ? created.id : parseInt(String(created.id), 10);
              const doc = {
                ...data,
                ...created,
                id: String(created.id),
                server_id: Number.isFinite(sid) ? sid : created.id,
              };
              await userDataStore.putCampaign(doc);
              return doc;
            }
          } catch (e) {
            // When a token-backed session exists (including guest mode), campaign create must stay server-backed.
            // Falling back to local-only campaigns causes downstream API features (notes, chat, etc.) to fail.
            throw e;
          }
        }
        return userDataStore.putCampaign(data);
      }

      if (!storageKey) {
        return { id: Date.now().toString(), ...data };
      }

      const isAuthenticated = hasRealAccountSession();
      const localOnly = options?.localOnly === true;
      
      if (!localOnly && API_AVAILABLE && isAuthenticated) {
        try {
          // Use the proper API client method for characters
          let newItem;
          newItem = await apiClient.post(`/api/${apiEntityName}`, data);
          
          if (offlineCache.isOnline()) {
            await offlineCache.cacheEntity(storageKey, newItem);
          }
          const items = getFromStorage(storageKey);
          items.push(newItem);
          saveToStorage(storageKey, items);
          return newItem;
        } catch (error) {
          if (isAuthError(error) && isAuthenticated) {
            throw new Error('Authentication expired. Please log in again.');
          }
          await offlineCache.addToSyncQueue({
            type: 'create',
            entity: apiEntityName,
            entityId: data.id || Date.now().toString(),
            data,
          });
        }
      }

      const items = getFromStorage(storageKey);
      const newItem = {
        id: Date.now().toString(),
        ...data,
        created_date: new Date().toISOString(),
      };
      items.push(newItem);
      saveToStorage(storageKey, items);
      try {
        await offlineCache.cacheEntity(storageKey, newItem);
      } catch (_) {}
      return newItem;
    },
    
    async update(id, data) {
      if (entityName === 'Character') {
        const existing = await resolveCharacterForUpdate(id);
        if (!existing) return null;
        const base = await hydrateLocalCharacterFromServer(existing);
        const result = await userDataStore.putCharacter({ ...base, ...data, id: base.id });
        if (shouldSyncCharactersToServer()) {
          try {
            const serverId = resolveLocalServerNumericId(result);
            const patchPayload = buildCharacterServerPatch(base, data);
            if (patchPayload && serverId != null) {
              try {
                const serverChar = await apiClient.updateCharacter(serverId, patchPayload);
                if (serverChar) {
                  const merged = mergeServerCharacterIntoLocal(result, serverChar);
                  const full = {
                    ...result,
                    ...merged,
                    id: result.id,
                    server_id: merged.server_id ?? serverId,
                  };
                  await userDataStore.putCharacter(full);
                  return full;
                }
              } catch (err) {
                if (!isNotFoundError(err)) throw err;
                const createPayload = buildCharacterServerPayload(result);
                if (createPayload) {
                  const serverChar = await apiClient.createCharacter(createPayload);
                  if (serverChar?.id) {
                    const withServer = mergeServerCharacterIntoLocal(
                      { ...result, server_id: parseInt(serverChar.id, 10) },
                      serverChar
                    );
                    const full = {
                      ...result,
                      ...withServer,
                      id: result.id,
                      server_id: withServer.server_id ?? parseInt(serverChar.id, 10),
                    };
                    await userDataStore.putCharacter(full);
                    return full;
                  }
                }
              }
            } else if (!serverId) {
              const createPayload = buildCharacterServerPayload(result);
              if (createPayload) {
                const serverChar = await apiClient.createCharacter(createPayload);
                if (serverChar?.id) {
                  const withServer = mergeServerCharacterIntoLocal(
                    { ...result, server_id: parseInt(serverChar.id, 10) },
                    serverChar
                  );
                  const full = {
                    ...result,
                    ...withServer,
                    id: result.id,
                    server_id: withServer.server_id ?? parseInt(serverChar.id, 10),
                  };
                  await userDataStore.putCharacter(full);
                  return full;
                }
              }
            }
          } catch {
            // Do not run triggerCharacterSyncToServer() here — full local snapshots can wipe server inventory.
          }
        }
        return result;
      }
      if (entityName === 'Campaign') {
        const existing = await userDataStore.getCampaign(id);
        if (!existing) return null;
        const merged = { ...existing, ...data, id: String(existing.id) };
        const isAuth = hasRealAccountSession();
        const online = typeof navigator !== 'undefined' && navigator.onLine;
        if (isAuth && online && USE_API !== 'false') {
          const serverId =
            existing.server_id != null
              ? Number(existing.server_id)
              : Number.isFinite(Number(id))
                ? Number(id)
                : null;
          if (serverId != null && !Number.isNaN(serverId)) {
            try {
              const updated = await apiClient.updateCampaign(String(serverId), {
                name: merged.name ?? existing.name,
                description: merged.description ?? existing.description ?? '',
                rule_settings: merged.rule_settings ?? existing.rule_settings ?? {},
              });
              const doc = {
                ...existing,
                ...merged,
                ...updated,
                id: String(updated.id ?? serverId),
                server_id: Number(updated.id ?? serverId),
              };
              await userDataStore.putCampaign(doc);
              return doc;
            } catch (e) {
              if (isAuthError(e)) throw e;
            }
          }
        }
        return userDataStore.putCampaign(merged);
      }

      if (!storageKey) {
        return { id, ...data };
      }

      if (API_AVAILABLE) {
        try {
          let updated;
          updated = await apiClient.put(`/api/${apiEntityName}/${id}`, data);
          
          // Update cache
          await offlineCache.cacheEntity(storageKey, updated);
          // Update localStorage
          const items = getFromStorage(storageKey);
          const index = items.findIndex(item => item.id === id);
          if (index !== -1) {
            items[index] = updated;
            saveToStorage(storageKey, items);
          }
          return updated;
        } catch (error) {
          if (isAuthError(error)) {
            const token = localStorage.getItem('auth_token');
            if (token) {
              throw new Error('Authentication expired. Please log in again.');
            }
          } else {
            await offlineCache.addToSyncQueue({
              type: 'update',
              entity: apiEntityName,
              entityId: id,
              data,
            });
          }
        }
      }

      // Fallback to localStorage
      const items = getFromStorage(storageKey);
      const index = items.findIndex(item => item.id === id);
      if (index !== -1) {
        const updated = { ...items[index], ...data, updated_date: new Date().toISOString() };
        items[index] = updated;
        saveToStorage(storageKey, items);
        // Cache in IndexedDB
        await offlineCache.cacheEntity(storageKey, updated);
        return updated;
      }
      return null;
    },
    
    async delete(id) {
      if (entityName === 'Character') {
        let existing = await userDataStore.getCharacter(id);
        if (!existing) {
          const numId = parseInt(id, 10);
          if (Number.isFinite(numId)) {
            const all = await userDataStore.getAllCharacters();
            existing = all.find((c) => c.server_id === numId) ?? null;
          }
        }
        const serverId = existing?.server_id ?? (Number.isFinite(Number(id)) ? Number(id) : null);
        const localId = existing?.id ?? id;
        if (shouldSyncCharactersToServer() && serverId) {
          try {
            await apiClient.deleteCharacter(serverId);
          } catch (err) {
            // If server already doesn't have it, treat as success and continue local cleanup.
            if (!isNotFoundError(err)) {
              throw err;
            }
          }
        }
        await userDataStore.removeCharacter(localId);
        try {
          await offlineCache.deleteCachedEntity('characters', localId);
          if (serverId != null) await offlineCache.deleteCachedEntity('characters', serverId);
        } catch {
          // non-fatal
        }
        return true;
      }
      if (entityName === 'Campaign') {
        let existing = await userDataStore.getCampaign(id);
        const numFromId = Number(id);
        if (!existing && Number.isFinite(numFromId)) {
          const all = await userDataStore.getAllCampaigns();
          existing = all.find((c) => Number(c.server_id) === numFromId) ?? null;
        }
        let serverId =
          existing?.server_id != null && existing.server_id !== ''
            ? Number(existing.server_id)
            : Number.isFinite(numFromId)
              ? numFromId
              : null;
        if (
          shouldSyncCharactersToServer() &&
          existing &&
          (serverId == null || Number.isNaN(serverId)) &&
          !Number.isFinite(numFromId)
        ) {
          try {
            const list = await apiClient.getCampaigns();
            const nameKey = String(existing.name || '').trim().toLowerCase();
            const resolved = pickServerCampaignForLocalReconcile(existing, nameKey, list);
            if (resolved?.id != null) serverId = Number(resolved.id);
          } catch {
            // ignore
          }
        }
        const localIdsToRemove = new Set();
        if (existing?.id != null) localIdsToRemove.add(String(existing.id));
        if (serverId != null && !Number.isNaN(serverId)) {
          const all = await userDataStore.getAllCampaigns();
          for (const r of all || []) {
            if (Number(r.server_id) === serverId && r.id != null) {
              localIdsToRemove.add(String(r.id));
            }
          }
        }
        if (localIdsToRemove.size === 0) localIdsToRemove.add(String(id));

        if (shouldSyncCharactersToServer() && serverId != null && !Number.isNaN(serverId)) {
          try {
            await apiClient.deleteCampaign(String(serverId));
          } catch (err) {
            if (!isNotFoundError(err)) {
              throw err;
            }
          }
        }
        for (const localId of localIdsToRemove) {
          await userDataStore.removeCampaign(localId);
          try {
            await offlineCache.deleteCachedEntity('campaigns', localId);
          } catch {
            // non-fatal
          }
        }
        if (serverId != null && !Number.isNaN(serverId)) {
          try {
            await offlineCache.deleteCachedEntity('campaigns', serverId);
          } catch {
            // non-fatal
          }
        }
        return true;
      }

      if (!storageKey) {
        return false;
      }

      if (API_AVAILABLE) {
        try {
          // Use the proper API client method for characters
          if (entityName === 'Character') {
            await apiClient.deleteCharacter(id);
          } else {
            await apiClient.delete(`/api/${apiEntityName}/${id}`);
          }
          
          // Remove from cache
          // (IndexedDB delete would go here if we add that method)
          // Update localStorage
          const items = getFromStorage(storageKey);
          const filtered = items.filter(item => item.id !== id);
          saveToStorage(storageKey, filtered);
          return true;
        } catch (error) {
          if (isAuthError(error)) {
            const token = localStorage.getItem('auth_token');
            if (token) {
              throw new Error('Authentication expired. Please log in again.');
            }
          } else {
            await offlineCache.addToSyncQueue({
              type: 'delete',
              entity: apiEntityName,
              entityId: id,
              data: {},
            });
          }
        }
      }

      // Fallback to localStorage
      const items = getFromStorage(storageKey);
      const filtered = items.filter(item => item.id !== id);
      saveToStorage(storageKey, filtered);
      return filtered.length < items.length;
    }
  };
}

async function getBeastsForWildShape(druidLevel, character) {
  const all = await entityApi.entities.Monster.list();
  const rules = character ? getWildShapeRules(character) : getBaseWildShapeRules(druidLevel);
  return (Array.isArray(all) ? all : [])
    .filter((m) => evaluateWildShapeCandidate(m, rules).eligible)
    .sort((a, b) => monsterChallengeRating(a) - monsterChallengeRating(b))
    .map((m) => ({ id: m.id, name: m.name, cr: monsterChallengeRating(m) }));
}

export const entityApi = {
  entities: {
    Character: createEntity('Character'),
    Campaign: createEntity('Campaign'),
    Race: createEntity('Race'),
    Class: createEntity('Class'),
    Subclass: createEntity('Subclass'),
    Spell: createEntity('Spell'),
    Background: createEntity('Background'),
    Monster: createEntity('Monster'),
    MagicItem: createEntity('MagicItem'),
    Feat: createEntity('Feat'),
    Language: createEntity('Language'),
    Combat: createEntity('Combat'),
    CombatParticipant: createEntity('CombatParticipant'),
    CombatEncounter: createEntity('CombatEncounter'),
  },
  getBeastsForWildShape,

  async getNames() {
    return loadNames();
  },

  async getTreasure() {
    return loadTreasure();
  },

  async getMagicItemTable() {
    return loadMagicItemTable();
  },
};

